import { getAxiosClient } from '../../client';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse } from '../../responses';
import { UsersEndpoints } from './usersEndpoints';
import type { IUsersService } from './IUsersService';
import type {
    CreateUserRequest,
    SetUserPasswordRequest,
    SetUserPinRequest,
    UpdateUserRequest,
    UserListQuery,
} from './requests';
import type { GetUsersResponse, UserWriteResponse } from './responses';

type UsersServiceOptions = {
    baseUrl: string;
};

export class UsersService implements IUsersService {
    private readonly baseUrl: string;

    constructor({ baseUrl }: UsersServiceOptions) {
        this.baseUrl = baseUrl;
    }

    public getUsers(
        query: UserListQuery,
    ): Promise<ApiResponse<GetUsersResponse>> {
        const url = UsersEndpoints.list();
        // The bos-api binds these case-insensitively; `search` is omitted when
        // blank so an empty box returns the unfiltered page. A BranchAdmin is
        // auto-scoped server-side, so `branchId` is the OrgAdmin's filter.
        const params: Record<string, string | number> = {
            page: query.page,
            pageSize: query.pageSize,
        };
        const search = query.search?.trim();
        if (search) {
            params.search = search;
        }
        if (query.branchId) {
            params.branchId = query.branchId;
        }
        return makeApiRequest<GetUsersResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url, { params }),
        );
    }

    public createUser(
        body: CreateUserRequest,
    ): Promise<ApiResponse<UserWriteResponse>> {
        const url = UsersEndpoints.create();
        return makeApiRequest<UserWriteResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).post(url, body),
        );
    }

    public updateUser(
        id: string,
        body: UpdateUserRequest,
    ): Promise<ApiResponse<UserWriteResponse>> {
        const url = UsersEndpoints.update(id);
        return makeApiRequest<UserWriteResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).patch(url, body),
        );
    }

    public setUserPin(
        id: string,
        body: SetUserPinRequest,
    ): Promise<ApiResponse<void>> {
        const url = UsersEndpoints.pin(id);
        return makeApiRequest<void>(
            getAxiosClient({ baseURL: this.baseUrl }).post(url, body),
        );
    }

    public setUserPassword(
        id: string,
        body: SetUserPasswordRequest,
    ): Promise<ApiResponse<void>> {
        const url = UsersEndpoints.password(id);
        return makeApiRequest<void>(
            getAxiosClient({ baseURL: this.baseUrl }).post(url, body),
        );
    }

    public deleteUser(id: string): Promise<ApiResponse<void>> {
        const url = UsersEndpoints.delete(id);
        return makeApiRequest<void>(
            getAxiosClient({ baseURL: this.baseUrl }).delete(url),
        );
    }
}
